import React, { useEffect, useRef, useState } from 'react';
import { Card, CardTitle, CardBody, Button, Input } from 'reactstrap';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';

const ChatBotPage = () => {
  const { user } = useSelector((state) => state.authState);

  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: "Hi! Try: 'attendance class 6A today' or 'attendance of Ram class 6A today'.",
    },
  ]);

  const bottomRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }),
      50
    );
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ✅ helper: take vendor/branch from user if present, otherwise null
  const vendorId =
    user?.vendor_id ??
    user?.Vendor_ID ??
    user?.vendorId ??
    user?.VendorId ??
    null;

  const branchId =
    user?.branch_id ??
    user?.Branch_ID ??
    user?.branchId ??
    user?.BranchId ??
    null;

  const onSend = async () => {
    const text = (input || '').trim();
    if (!text || busy) return;

    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    setBusy(true);

    try {
      const headers = {
        Token_ID: user?.token_id,
        Token_Data: user?.token_data,
      };

      // ✅ vendor/branch optional: only append if available
      const qs = new URLSearchParams();
      if (vendorId != null && vendorId !== '') qs.set('vendorId', vendorId);
      if (branchId != null && branchId !== '') qs.set('branchId', branchId);

      const url = qs.toString()
        ? `api/ChatBot/ask?${qs.toString()}`
        : `api/ChatBot/ask`;

      const res = await apiService.post(url, { message: text }, { headers });

      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: res?.message || (res?.status ? 'OK' : 'Failed'),
          payload: res,
        },
      ]);
    } catch (e) {
      console.error(e);
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: 'Something went wrong' },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSend();
    }
  };

  const onQuick = (txt) => setInput(txt);

  return (
    <>
      <div className='text-right mb-2'>
        <Button
          color='primary'
          onClick={() => onQuick('attendance class 6A today')}
        >
          <i className='bi bi-lightning-charge' /> Quick: Attendance Today
        </Button>
      </div>

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0'>
          <i className='bi bi-robot mr-2' /> ChatBot
        </CardTitle>

        <CardBody>
          <div
            style={{
              height: 420,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 8,
              padding: 12,
              background: '#fafafa',
            }}
          >
            {messages.map((m, idx) => (
              <ChatBubble key={idx} msg={m} />
            ))}
            <div ref={bottomRef} />
          </div>

          <div className='d-flex mt-3' style={{ gap: 10 }}>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder='Type here... e.g. attendance class 6A today'
              disabled={busy}
            />
            <Button
              color='dark'
              onClick={onSend}
              disabled={busy || !input.trim()}
            >
              {busy ? '...' : 'Send'}
            </Button>
          </div>

          <div className='mt-3 d-flex flex-wrap' style={{ gap: 8 }}>
            <Button
              outline
              color='secondary'
              size='sm'
              onClick={() => onQuick('attendance class 6A today')}
            >
              attendance class 6A today
            </Button>
            <Button
              outline
              color='secondary'
              size='sm'
              onClick={() => onQuick('attendance of Ram class 6A today')}
            >
              attendance of Ram class 6A today
            </Button>
            <Button
              outline
              color='secondary'
              size='sm'
              onClick={() =>
                onQuick(
                  'attendance report class 6A from 2026-01-01 to 2026-01-10'
                )
              }
            >
              attendance report range
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default ChatBotPage;

// ---------- same renderers ----------
const ChatBubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div
      className={`d-flex mb-2 ${
        isUser ? 'justify-content-end' : 'justify-content-start'
      }`}
    >
      <div
        style={{
          maxWidth: '78%',
          padding: '10px 12px',
          borderRadius: 12,
          border: '1px solid #eee',
          background: isUser ? '#d1f7d6' : '#fff',
          whiteSpace: 'pre-wrap',
        }}
      >
        <div>{msg.text}</div>

        {!isUser && msg.payload && (
          <div
            style={{
              marginTop: 10,
              borderTop: '1px dashed #ddd',
              paddingTop: 10,
            }}
          >
            <SmallMeta payload={msg.payload} />
            <BotDataRenderer payload={msg.payload} />
          </div>
        )}
      </div>
    </div>
  );
};

const SmallMeta = ({ payload }) => (
  <div className='d-flex align-items-center' style={{ gap: 10, fontSize: 12 }}>
    <span className='badge bg-primary'>{payload?.intent || 'None'}</span>
    <span style={{ opacity: 0.7 }}>
      conf: {(payload?.confidence ?? 0).toFixed(2)}
    </span>
  </div>
);

const BotDataRenderer = ({ payload }) => {
  const data = payload?.data;

  const isAttendanceView =
    data &&
    typeof data === 'object' &&
    (data.totalStudents != null || data.TotalStudents != null);

  const isReportList =
    Array.isArray(data) &&
    data.length > 0 &&
    (data[0].attendancePercentage != null ||
      data[0].AttendancePercentage != null);

  return (
    <>
      {isAttendanceView && <AttendanceViewCard dto={data} />}
      {isReportList && <AttendanceReportTable rows={data} />}
      {Array.isArray(payload?.followUpQuestions) &&
        payload.followUpQuestions.length > 0 && (
          <div className='mt-2' style={{ fontSize: 12 }}>
            <div style={{ opacity: 0.7 }}>Try:</div>
            <ul style={{ marginBottom: 0 }}>
              {payload.followUpQuestions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </div>
        )}
    </>
  );
};

const AttendanceViewCard = ({ dto }) => {
  const classId = dto.classId ?? dto.ClassId ?? '';
  const date = dto.date ?? dto.Date ?? '';

  const total = dto.totalStudents ?? dto.TotalStudents ?? 0;
  const present = dto.totalPresent ?? dto.TotalPresent ?? 0;
  const absent = dto.totalAbsent ?? dto.TotalAbsent ?? 0;

  const absentees = dto.absentees ?? dto.Absentees ?? [];

  return (
    <div
      className='mt-3'
      style={{ border: '1px solid #eee', borderRadius: 10, padding: 10 }}
    >
      <div style={{ fontWeight: 700 }}>
        Class {classId} • {String(date)}
      </div>
      <div className='d-flex mt-2' style={{ gap: 10, flexWrap: 'wrap' }}>
        <span className='badge bg-secondary'>Total: {total}</span>
        <span className='badge bg-success'>Present: {present}</span>
        <span className='badge bg-danger'>Absent: {absent}</span>
      </div>

      <div className='mt-3' style={{ fontWeight: 700 }}>
        Absentees
      </div>
      {absentees.length === 0 ? (
        <div style={{ fontSize: 13, opacity: 0.7 }}>No absentees</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className='table table-sm table-striped mt-2'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Enroll No</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {absentees.map((x, i) => (
                <tr key={i}>
                  <td>{x.studentName ?? x.StudentName ?? '-'}</td>
                  <td>{x.enrollmentNo ?? x.EnrollmentNo ?? '-'}</td>
                  <td>{x.reason ?? x.Reason ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AttendanceReportTable = ({ rows }) => (
  <div
    className='mt-3'
    style={{ border: '1px solid #eee', borderRadius: 10, padding: 10 }}
  >
    <div style={{ fontWeight: 700 }}>Attendance Report</div>
    <div style={{ overflowX: 'auto' }}>
      <table className='table table-sm table-striped mt-2'>
        <thead>
          <tr>
            <th>Student</th>
            <th>Working</th>
            <th>Present</th>
            <th>Absent</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.studentName ?? r.StudentName ?? '-'}</td>
              <td>{r.totalWorkingDays ?? r.TotalWorkingDays ?? 0}</td>
              <td>{r.presentDays ?? r.PresentDays ?? 0}</td>
              <td>{r.absentDays ?? r.AbsentDays ?? 0}</td>
              <td>{r.attendancePercentage ?? r.AttendancePercentage ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
